// Settings → Team: invite members, change roles, disable seats.
// Invites create a Cognito account — the new member gets a temp-password
// email and chooses their own password on first sign-in.

import { useEffect, useRef, useState } from "react";
import { inviteMember, patchMember, reassignMemberDeals, resendInvite } from "../api";
import { useAppData, useMe } from "../store";
import type { TeamMember } from "../types";
import { Avatar, Btn, TextInput } from "../components/Primitives";
import { Select } from "../components/Select";
import { Ic } from "../components/Icon";

const ROLES = ["Admin", "Partner", "Principal", "Associate"];

const STATUS_STYLE: Record<TeamMember["status"], { label: string; color: string; bg: string }> = {
  active:   { label: "Active",   color: "var(--pos)",   bg: "var(--pos-soft)" },
  invited:  { label: "Invited",  color: "var(--warn)",  bg: "var(--warn-soft)" },
  demo:     { label: "Demo",     color: "var(--ink-3)", bg: "var(--raised)" },
  disabled: { label: "Disabled", color: "var(--neg)",   bg: "var(--neg-soft)" },
};

function StatusChip({ status }: { status: TeamMember["status"] }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.active;
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 500,
      color: s.color, background: s.bg, whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
}

function RowMenu({
  member, busy, onRole, onStatus, onResend, isSelf,
}: {
  member: TeamMember;
  busy: boolean;
  isSelf: boolean;
  onRole: (role: string) => void;
  onStatus: (status: "active" | "disabled") => void;
  onResend: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const item = (label: string, onClick: () => void, danger = false, on = false) => (
    <div
      key={label}
      onClick={() => { setOpen(false); onClick(); }}
      style={{
        padding: "7px 12px", borderRadius: 5, cursor: "pointer", fontSize: 12.5,
        color: danger ? "var(--neg)" : "var(--ink)", display: "flex",
        alignItems: "center", gap: 8,
        background: on ? "var(--bg)" : "transparent",
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      {on && <Ic name="check" size={12} />}
    </div>
  );

  return (
    <div ref={ref} style={{ position: "relative", textAlign: "right" }}>
      <span
        style={{ color: "var(--ink-3)", cursor: busy ? "wait" : "pointer" }}
        onClick={() => !busy && setOpen((o) => !o)}
      >
        <Ic name="dots" size={14} />
      </span>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 4px)", width: 190,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 8, boxShadow: "var(--shadow-pop)", padding: 4,
          zIndex: 60, textAlign: "left",
        }}>
          <div style={{
            padding: "6px 12px 4px", fontSize: 10.5, color: "var(--ink-3)",
            textTransform: "uppercase", letterSpacing: "0.07em",
          }}>Role</div>
          {ROLES.map((r) => item(r, () => onRole(r), false, member.role === r))}
          {member.status === "invited" && (
            <>
              <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
              {item("Resend invite", onResend)}
            </>
          )}
          {!isSelf && <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />}
          {!isSelf && member.status !== "disabled"
            && item("Disable account", () => onStatus("disabled"), true)}
          {!isSelf && member.status === "disabled"
            && item("Re-enable account", () => onStatus("active"))}
        </div>
      )}
    </div>
  );
}

interface Props { onJumpToInbox: (memberId: string) => void; }

export function TeamScreen({ onJumpToInbox }: Props) {
  const { data, refresh } = useAppData();
  const me = useMe();
  const team = data?.config.team ?? [];

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName,  setInviteName]  = useState("");
  const [inviteRole,  setInviteRole]  = useState("Partner");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [disabling, setDisabling] = useState<TeamMember | null>(null);
  const [reassignTo, setReassignTo] = useState<string>("");

  const invite = async () => {
    if (!inviteEmail.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await inviteMember({
        email: inviteEmail.trim(),
        name: inviteName.trim(),
        role: inviteRole,
      });
      await refresh();
      setInviteEmail("");
      setInviteName("");
      setShowInvite(false);
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setSaving(false);
    }
  };

  const change = async (id: string, changes: { role?: string; status?: string }) => {
    setBusyId(id);
    setError(null);
    try {
      await patchMember(id, changes);
      await refresh();
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusyId(null);
    }
  };

  const resend = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      const r = await resendInvite(id);
      setNotice(`Invite re-sent to ${r.email}.`);
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusyId(null);
    }
  };

  // Disabling someone with active deals first reassigns them.
  const requestDisable = (m: TeamMember) => {
    if (m.owned > 0) {
      const fallback = team.find((t) => t.id !== m.id && t.status !== "disabled");
      setReassignTo(fallback?.id ?? "");
      setDisabling(m);
    } else {
      void change(m.id, { status: "disabled" });
    }
  };

  const confirmDisable = async () => {
    if (!disabling || !reassignTo) return;
    setBusyId(disabling.id);
    setError(null);
    try {
      await reassignMemberDeals(disabling.id, reassignTo);
      await patchMember(disabling.id, { status: "disabled" });
      await refresh();
      setDisabling(null);
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page">
      <div className="topbar">
        <span className="crumb">Settings</span>
        <span className="crumb-sep">/</span>
        <h1>Team</h1>
        <span className="crumb">· {team.length} members</span>
        <span className="grow" />
        <Btn size="sm" ico="plus" kind="primary" onClick={() => setShowInvite((x) => !x)}>
          Invite
        </Btn>
      </div>
      <div className="scroll" style={{ padding: "22px 28px 60px" }}>
        {showInvite && (
          <div style={{
            padding: 16, background: "var(--surface)",
            border: "1px solid var(--border)", borderRadius: 8,
            marginBottom: 14, display: "flex", gap: 12, alignItems: "center",
          }}>
            <TextInput
              placeholder="email@firm.vc" value={inviteEmail}
              onChange={setInviteEmail} style={{ flex: 1.2 }}
            />
            <TextInput
              placeholder="Full name" value={inviteName}
              onChange={setInviteName} style={{ flex: 1 }}
            />
            <Select
              value={inviteRole} onChange={setInviteRole}
              options={ROLES}
            />
            <Btn kind="primary" onClick={invite} disabled={saving}>
              {saving ? "Inviting…" : "Send invite"}
            </Btn>
          </div>
        )}
        {error && (
          <div style={{
            color: "var(--neg)", background: "var(--neg-soft)",
            padding: "8px 12px", borderRadius: 6, fontSize: 12.5, marginBottom: 14,
          }}>{error}</div>
        )}
        {notice && !error && (
          <div style={{
            color: "var(--pos)", background: "var(--pos-soft)",
            padding: "8px 12px", borderRadius: 6, fontSize: 12.5, marginBottom: 14,
          }}>{notice}</div>
        )}
        {disabling && (
          <div style={{
            padding: 16, background: "var(--surface)",
            border: "1px solid var(--border)", borderRadius: 8,
            marginBottom: 14, display: "flex", gap: 12, alignItems: "center",
            fontSize: 13,
          }}>
            <span>
              <strong>{disabling.name}</strong> owns {disabling.owned} active
              deal{disabling.owned === 1 ? "" : "s"} — reassign to
            </span>
            <Select
              value={reassignTo} onChange={setReassignTo}
              options={team.filter((t) => t.id !== disabling.id && t.status !== "disabled")
                .map((t) => ({ value: t.id, label: t.name }))}
            />
            <Btn kind="primary" size="sm" disabled={!reassignTo || busyId === disabling.id}
                 onClick={confirmDisable}>
              Reassign & disable
            </Btn>
            <Btn kind="ghost" size="sm" onClick={() => setDisabling(null)}>Cancel</Btn>
          </div>
        )}
        <div style={{
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 8, overflow: "visible",
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "32px 1.5fr 1.5fr 0.9fr 90px 110px 30px",
            gap: 12, padding: "12px 18px",
            fontSize: 10.5, color: "var(--ink-3)",
            textTransform: "uppercase", letterSpacing: "0.08em",
            fontWeight: 500, borderBottom: "1px solid var(--border)",
            background: "var(--bg)",
          }}>
            <span /><span>Member</span><span>Email</span><span>Role</span><span>Status</span><span>Active deals</span><span />
          </div>
          {team.map((m, i) => (
            <div key={m.id} className="row" style={{
              display: "grid",
              gridTemplateColumns: "32px 1.5fr 1.5fr 0.9fr 90px 110px 30px",
              gap: 12, padding: "14px 18px",
              borderBottom: i < team.length - 1 ? "1px solid var(--border)" : "none",
              alignItems: "center", fontSize: 13,
              opacity: m.status === "disabled" ? 0.55 : 1,
            }}>
              <Avatar id={m.id} size="md" />
              <span style={{ fontWeight: 500 }}>
                {m.name}
                {m.id === me?.id && (
                  <span style={{ color: "var(--ink-3)", fontWeight: 400 }}> · you</span>
                )}
              </span>
              <span style={{
                color: "var(--ink-2)", fontSize: 12.5,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{m.email}</span>
              <span style={{ color: "var(--ink-2)" }}>{m.role}</span>
              <span><StatusChip status={m.status} /></span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="score" style={{ fontSize: 14 }}>{m.owned}</span>
                <span style={{
                  color: "var(--ink-3)", fontSize: 11.5, cursor: "pointer",
                  textDecoration: "underline", textUnderlineOffset: 2,
                  textDecorationColor: "var(--ink-4)",
                }} onClick={() => onJumpToInbox(m.id)}>view in inbox</span>
              </div>
              <RowMenu
                member={m}
                busy={busyId === m.id}
                isSelf={m.id === me?.id}
                onRole={(role) => void change(m.id, { role })}
                onStatus={(status) =>
                  status === "disabled" ? requestDisable(m) : void change(m.id, { status })}
                onResend={() => void resend(m.id)}
              />
            </div>
          ))}
        </div>
        <p style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 14, lineHeight: 1.6 }}>
          Invited members receive a temporary password by email and set their
          own on first sign-in. Demo members are seeded sample teammates — they
          can own deals but can't sign in until you invite their email.
        </p>
      </div>
    </div>
  );
}
